import * as servicesActions from "@rentiohq/shared-frontend/dist/redux/services/services.actions";
import * as servicesApi from "@rentiohq/shared-frontend/dist/redux/services/services.api";
import * as documentApi from "@rentiohq/shared-frontend/dist/reduxV2/documents/document.api";
import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Button,
  Form,
  Stack,
} from "@rentiohq/web-shared-next/dist/ui-components";
import { enqueueSnackbar } from "@rentiohq/web-shared-next/dist/utils/snackbar";
import { EButtonVariants } from "@rentiohq/web-shared-next/dist/utils/types/button";
import React from "react";
import { useDispatch } from "react-redux";
import { ReportProblemSuccessModal } from "tenant-dashboard/components/Modals/ReportProblemSuccessModal";
import { reportProblemSchema } from "../../../../forms/reportProblem";

export interface IProps {
  property: IProperty;
  handleClose: () => void;
}

export const ReportProblemForm = ({ property, handleClose }: IProps) => {
  const dispatch = useDispatch();

  const [showSuccessModal, setShowSuccessModal] = React.useState(false);

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    handleClose();
  };

  const handleAttachments = async (reportId: number, attachments: File[]) => {
    try {
      const uploadPromises = attachments.map(async attachment => {
        try {
          // @ts-ignore
          const presignedUrl = await documentApi.uploadDocumentToS3(attachment);

          if (!presignedUrl) {
            enqueueSnackbar({
              message: getLocalizedText(
                "tenant.report_problem.not_presigned_url",
                { fileName: attachment.name },
              ),
              variant: "error",
            });
            return;
          }
          await servicesApi.reportProblemAddAttachment(reportId, {
            preSignedUrl: presignedUrl,
            filename: attachment.name || "file",
          });
        } catch (error: any) {
          enqueueSnackbar({
            message:
              error.message ||
              getLocalizedText("tenant.report_problem.file_processing_error", {
                fileName: attachment.name,
              }),
            variant: "error",
          });
        }
      });

      await Promise.all(uploadPromises);

      setShowSuccessModal(true);
    } catch (error: any) {
      enqueueSnackbar({
        message: error?.message || getLocalizedText("general.error"),
        variant: "error",
      });
    }
  };

  const handleSuccess = (reportId?: number, attachments?: File[]) => {
    if (reportId && attachments && attachments.length > 0) {
      handleAttachments(reportId, attachments);
    } else {
      setShowSuccessModal(true);
    }
  };

  const handleSubmit = (values: any) => {
    dispatch(
      servicesActions.reportProblem.actions.start({
        data: {
          propertyId: property.id,
          ...values,
        },
        onSuccess: (id?: number) => handleSuccess(id, values.attachments),
        onFailure: (error: Error) => {
          enqueueSnackbar({
            message: getLocalizedText(error.message || "system.error"),
            variant: "error",
          });
        },
      }),
    );
  };

  const renderActions = () => (
    <Stack justifySelf="flex-end">
      <Button variant={EButtonVariants.Contained} type="submit">
        {getLocalizedText("system.send")}
      </Button>
    </Stack>
  );

  return (
    <>
      <Form
        getSchema={reportProblemSchema()}
        onSubmit={handleSubmit}
        asMultiStep={false}
        showSummary={false}
        customActions={renderActions()}
      />
      <ReportProblemSuccessModal
        open={showSuccessModal}
        handleClose={handleSuccessModalClose}
      />
    </>
  );
};
